import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../client';
import { queryKeys } from './keys';
import type { RejectPreviewOut } from '../generated/schemas';
import type { Industry } from '../types';

export function useIndustries({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.industries,
    queryFn: () => api<{ industries: Industry[] }>('/api/industries'),
    enabled,
  });
}

function useInvalidateIndustries() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: queryKeys.industries });
    void client.invalidateQueries({ queryKey: ['ideas'] });
    void client.invalidateQueries({ queryKey: queryKeys.dashboard });
  };
}

export function useSaveIndustry() {
  const invalidate = useInvalidateIndustries();
  return useMutation({
    meta: {
      success: (industry: Industry, { id, change }: { id?: string; change: Partial<Industry> }) =>
        !id
          ? `${industry.name} added`
          : Object.keys(change).length === 1 && change.is_active !== undefined
            ? `${industry.name} ${industry.is_active ? 'reactivated' : 'deactivated'}`
            : `${industry.name} saved`,
    },
    mutationFn: ({
      id,
      change,
    }: {
      id?: string;
      change: Partial<Pick<Industry, 'name' | 'description' | 'is_active'>>;
    }) => (id ? api<Industry>(`/api/industries/${id}`, 'PATCH', change) : api<Industry>('/api/industries', 'POST', change)),
    onSuccess: invalidate,
  });
}

export function useApproveIndustry() {
  const invalidate = useInvalidateIndustries();
  return useMutation({
    meta: { success: (industry: Industry) => `${industry.name} approved` },
    mutationFn: (id: string) => api<Industry>(`/api/industries/${id}/approve`, 'POST'),
    onSuccess: invalidate,
  });
}

/** What rejecting a proposal would touch now: the dialog says so before the reviewer chooses a replacement. */
export type RejectPreview = RejectPreviewOut;

export interface RejectResult {
  replacement: Industry | null;
  ideas_reassigned: number;
  assets_retagged?: number;
  cases_retagged?: number;
}

export function useRejectPreview(id: string) {
  return useQuery({
    queryKey: [...queryKeys.industries, id, 'reject-preview'],
    queryFn: () => api<RejectPreview>(`/api/industries/${id}/reject-preview`),
  });
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** The toast after a rejection, from the counts the server answered (they may differ from the preview). */
export function rejectedMessage(result: RejectResult): string {
  const moved = [
    result.ideas_reassigned ? plural(result.ideas_reassigned, 'idea', 'ideas') : '',
    result.assets_retagged ? plural(result.assets_retagged, 'asset', 'assets') : '',
    result.cases_retagged ? plural(result.cases_retagged, 'case', 'cases') : '',
  ].filter(Boolean);
  return moved.length ? `Industry rejected; ${moved.join(', ')} moved` : 'Industry rejected';
}

export function useRejectIndustry() {
  const invalidate = useInvalidateIndustries();
  return useMutation({
    meta: { success: rejectedMessage },
    mutationFn: ({ id, replacement_industry_id }: { id: string; replacement_industry_id: string | null }) =>
      api<RejectResult>(`/api/industries/${id}/reject`, 'POST', { replacement_industry_id }),
    onSuccess: invalidate,
  });
}

/** Propose an industry: an existing name in any case comes back as it is, a new one waits for review. */
export function useSuggestIndustry() {
  const invalidate = useInvalidateIndustries();
  return useMutation({
    meta: {
      success: (industry: Industry, name: string) =>
        industry.status === 'pending' && industry.name === name.trim()
          ? `${industry.name} suggested; it waits for review`
          : `${industry.name} already exists`,
    },
    mutationFn: (name: string) => api<Industry>('/api/industries/suggest', 'POST', { name }),
    onSuccess: invalidate,
  });
}
